import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_EditActionButtons } from 'material-react-table';
import apiClient from '../api/client';
import { getVendorName, vendors } from "../utils";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { DialogActions, DialogTitle, DialogContent } from '@mui/material';

export default function SkuMapping({ domain, skuCode }) {

  const [data, setMappings] = useState([]);
  const [creationError, setCreationError] = useState('');

	 const columns = useMemo(
        () => [
          {
            accessorKey: 'lower_domain_name',
            header: 'Source Vendor',
            size: 150,
            editVariant: 'select',
            editSelectOptions: vendors
          },
          {
            accessorKey: 'lower_product_code',
            header: 'Source Code',
            size: 150
          },
          {       
              accessorKey: 'upper_domain_name',
              header: 'Destination Vendor',
              size: 150,
              editVariant: 'select',
              editSelectOptions: vendors
          },
          {       
              accessorKey: 'upper_product_code',
              header: 'Destination Code',
              size: 150
          },
          {       
              accessorKey: 'creation_date',
              header: 'Date Matched',
              size: 150
          },
          ],
        []);

    const getSKUDetails = useCallback(async () => {
        try {
            const url = '/sku-mapping?sku=' + skuCode + "&domain=" + encodeURIComponent(domain);
            const res = await apiClient.get(url);
            const data = (res.data || []).map(item => ({
                ...item,
                upper_domain_name: getVendorName(item.upper_domain_id),
                lower_domain_name: getVendorName(item.lower_domain_id)
            }));
            setMappings(data);
        } catch (error) {
            console.error('Error fetching SKU mappings:', error);
        }
    }, [domain, skuCode]);

    const handleCellClick = (cell) => {
      const columnId = cell.column.id;
      if (columnId !== "lower_product_code" && columnId !== "upper_product_code") {
        return;
      }

      const prefix = columnId === "lower_product_code" ? "lower" : "upper";
      const domain = cell.row.original[prefix + "_domain_name"];
      const sku = cell.getValue();

      if (domain && sku) {
        const url = "Sku?domain=" + encodeURIComponent(domain) + "&sku=" + encodeURIComponent(sku);
        window.open(url, "_blank");
      }
    };

    const openDeleteConfirmModal = (row) => {
        if (window.confirm('Are you sure you want to delete this mapping?')) {
            console.log(row.original);
        }
    };

    const createSkuMapping = async ({ values, table }) => {
        setCreationError('');
        try {
            const url = '/sku-mapping';
            const payload = {
                "source_domain": values.lower_domain_name,
                "source_sku": values.lower_product_code,
                "destination_domain": values.upper_domain_name,
                "destination_sku": values.upper_product_code
            };
            
            await apiClient.put(url, payload);
            getSKUDetails();
            table.setCreatingRow(null);
        } catch (error) {
            setCreationError(
                error.response?.data?.message ||
                'Unable to create SKU mapping. Please try again.'
            );
            console.error('Error creating SKU mapping:', error);
        }
    };

	 const table = useMaterialReactTable({
        columns,
        data,
        enableTopToolbar: true,
        enableBottomToolbar: false,
        enableEditing: true,
        renderEmptyRowsFallback: () => (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No vendor SKU mappings established for this product yet.
            </Typography>
          </Box>
        ),
        createDisplayMode: 'modal',
        onCreatingRowSave: createSkuMapping,
        onCreatingRowCancel: () => setCreationError(''),
        muiTablePaperProps: {
            elevation: 0,
            sx: {
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 'none',
            },
        },
        muiTableBodyCellProps: ({ cell }) => ({
          onClick: () => handleCellClick(cell),
        }),
        renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
          <>
            <DialogTitle variant="h5" sx={{ fontWeight: 600 }}>Create SKU Mapping</DialogTitle>
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', pt: 2 }}
            >
              {creationError && <Alert severity="error">{creationError}</Alert>}
              {internalEditComponents}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <MRT_EditActionButtons variant="text" table={table} row={row} />
            </DialogActions>
          </>
        ),
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Tooltip title="Delete">
                <IconButton color="error" size="small" onClick={() => openDeleteConfirmModal(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
          renderTopToolbarCustomActions: ({ table }) => (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreationError('');
                table.setCreatingRow(true);
              }}
              sx={{ ml: 1 }}
            >
              Create SKU Mapping
            </Button>
          ),
	});

	  useEffect(() => {
        	getSKUDetails();
    }, [getSKUDetails]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Vendor SKU Mappings
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Cross-referenced matches between source and destination distributor feeds
        </Typography>
      </Box>

      <MaterialReactTable table={table} />
    </Box>
  );
}
